/**
 * FILE-OVERVIEW:
 * - 役割: イベント表示補助、ダウンロード、ビュー補助などの共通処理。
 * - 主な連携: components/Calendar/*, components/Memo/*
 * - 本番運用上の注意: 表示加工ロジック変更時は月/週/日/リストすべてで確認する。
 */

import React from 'react';
import { Typography, Box } from '@mui/material';
import { scheduleEventColors } from './colors';
import { uploadData } from '@aws-amplify/storage';
import { buildMemoNewsML2CTS } from './newsmlBuilder';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from '@aws-amplify/auth';
import { OUTPUT_BUCKET_NAME, S3_REGION } from '../services/s3Service';

/**
 * カレンダービュー切替ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @returns {(view:string|Object) => void}
 */
export const createViewChangeHandler = (calendarState) => {
  return (view) => {
    let viewType;
    if (typeof view === 'object' && view.view && view.view.type) {
      viewType = view.view.type;
    } else if (typeof view === 'string') {
      viewType = view;
    } else {
      console.error('無効なビュータイプ:', view);
      return;
    }
    
    calendarState.setCurrentView(viewType);
    
    if (calendarState.calendarRef.current) {
      const calendarApi = calendarState.calendarRef.current.getApi();
      if (calendarApi) {
        calendarApi.changeView(viewType);
      }
    }
  };
};

/**
 * FullCalendar向けのイベント描画関数を生成する。
 *
 * @param {string} currentView 現在のビュー種別
 * @returns {(eventInfo:Object) => React.ReactNode}
 *
 * 本番運用上の注意:
 * - 本描画は月/週/日/リストで分岐が多いため、見た目変更時は全ビューで回帰確認する。
 */
export const createEventContentRenderer = (currentView) => {
  return (eventInfo) => {
    const area = eventInfo.event.extendedProps?.area || '本社';
    const category = eventInfo.event.extendedProps?.category || '社会';
    const person = eventInfo.event.extendedProps?.person || '';
    const title = eventInfo.event.title || '';
    const notes = eventInfo.event.extendedProps?.notes || '';
    const isPrivateCategory = category === 'プライベート';
    const isListView = currentView.startsWith('list');
    const isDayView = currentView === 'dayGridDay';
    const isMonthOrWeekView = currentView === 'dayGridMonth' || currentView === 'dayGridWeek';
    const hasStartTime = Boolean(eventInfo.event.extendedProps?.hasStartTime);
    const hasEndTime = Boolean(eventInfo.event.extendedProps?.hasEndTime);
    // レスポンシブ判定: モバイル/タブレット(～1023px) / PC(1024px～)
    const isMobile = window.innerWidth <= 1023;
    const isAomoriArea = area === '青森';
    const isSoshikyokuArea = area === '総支局';
    const isHeadOfficeArea = !isPrivateCategory && !isAomoriArea && !isSoshikyokuArea;
    const areaTagText = isPrivateCategory
      ? 'プライベート'
      : isAomoriArea
      ? '青森'
      : isSoshikyokuArea
        ? '総支局'
        : isHeadOfficeArea
          ? '本社'
          : '';
    const showAreaTag = Boolean(areaTagText) && !isListView;
    const areaTagBackgroundColor = isPrivateCategory
      ? '#d50000'
      : isSoshikyokuArea
      ? '#0b8043'
      : isAomoriArea
        ? '#4986e7'
        : isHeadOfficeArea
          ? '#ef6c00'
          : '#212121';
    const eventBorderColor = isPrivateCategory
      ? scheduleEventColors.privateBorder
      : isSoshikyokuArea
        ? '#0b8043'
        : isAomoriArea
          ? '#4986e7'
          : isHeadOfficeArea
            ? '#ef6c00'
            : scheduleEventColors.border;

    const startDate = eventInfo.event.start ? new Date(eventInfo.event.start) : null;
    const endDate = (() => {
      if (!eventInfo.event.end) return null;
      const rawEnd = new Date(eventInfo.event.end);
      if (Number.isNaN(rawEnd.getTime())) return null;
      // 終日イベントの end は FullCalendar の排他的終端（+1日）のため1日戻す
      return eventInfo.event.allDay
        ? new Date(rawEnd.getTime() - 24 * 60 * 60 * 1000)
        : rawEnd;
    })();
    const formatTimeLabel = (date) => {
      if (!date || Number.isNaN(date.getTime())) return '';
      const h = String(date.getHours());
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${h}:${min}`;
    };
    const startTimeLabel = formatTimeLabel(startDate);
    const endTimeLabel = formatTimeLabel(endDate);
    const timeRangeContent = (() => {
      if (hasStartTime && hasEndTime) {
        return `${startTimeLabel}〜${endTimeLabel}`;
      }
      if (!hasStartTime && !hasEndTime) {
        return '終日';
      }
      if (hasStartTime && !hasEndTime) {
        return `${startTimeLabel}〜`;
      }
      if (!hasStartTime && hasEndTime) {
        return `〜${endTimeLabel}`;
      }
      return '';
    })();

    return (
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: isPrivateCategory ? scheduleEventColors.privateText : scheduleEventColors.text,
          border: isListView
            ? 'none'
            : `1px solid ${eventBorderColor}`,
          borderRadius: isListView ? '0' : '3px',
          '&:hover': {
            border: isListView
              ? 'none'
              : `1px solid ${eventBorderColor}`,
          },
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative',
          textAlign: 'left',
          height: '100%',
          minHeight: isListView ? 'auto' : '20px',
          overflow: 'hidden',
        }}
      >
        {showAreaTag && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              px: 0.8,
              py: 0.4,
              backgroundColor: areaTagBackgroundColor,
              color: '#ffffff',
              fontWeight: 700,
              fontSize: isMobile ? '0.72rem' : '0.82rem',
              lineHeight: 1.2,
              borderRadius: '0 0 3px 0',
              zIndex: 1,
            }}
          >
            {areaTagText}
          </Box>
        )}

        <Box
          sx={{
            p: isMobile && (isMonthOrWeekView) ? 0.5 : 1,
            pt: showAreaTag ? (isMobile ? 3.2 : 3.4) : undefined,
            mx: 0,
            width: '100%',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.2,
          }}
        >
          <Box
            sx={{
              fontSize: isMobile && (isMonthOrWeekView)
                ? '0.7rem'
                : '0.875rem',
              fontWeight: 500,
            }}
          >
            {(isMobile && isMonthOrWeekView)
              ? <Box sx={{ height: '0.2em', minHeight: '8px' }}>&nbsp;</Box>
              : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  <Box>{timeRangeContent}</Box>
                  <Box>
                    【{category}】
                    {!isPrivateCategory && ((title.length > 10 && !(isDayView || isListView))
                      ? `${title.substring(0, 10)}...`
                      : title)}
                  </Box>
                  <Box>{person}</Box>
                </Box>
              )
            }
          </Box>

          {/* リストビューでの編注表示 */}
          {isListView && notes && (
            <Box 
              sx={{ 
                mt: 1, 
                fontSize: '0.75rem', 
                color: '#666666',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              編注: {notes}
            </Box>
          )}
        </Box>
      </Typography>
    );
  };
};

/**
 * 編集中イベントの複製ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @returns {() => void}
 */
export const createDuplicateHandler = (calendarState) => {
  return () => {
    calendarState.setTempEvent({
      ...calendarState.tempEvent,
      id: null,
      title: `${calendarState.tempEvent.title} (複製)`,
      date: calendarState.tempEvent.date || calendarState.tempEvent.start
    });
  };
};

/**
 * 編集中イベントから NewsML2CTS XML を生成し、S3へ出稿するハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @returns {() => Promise<void>}
 *
 * 副作用:
 * - S3へ PutObject を実行し、結果を alert/console に通知する。
 */

export const createPublishMemoHandler = (calendarState) => {
  return async () => {
    try {
      const ev = calendarState.tempEvent || {};
      const eventId = ev.id || ev._id || 'noid';

      // NewsML2CTS 形式へ統一
      const xml = buildMemoNewsML2CTS({
        themeName: (ev.title || '').trim(),
        themeContent: (ev.notes || '').trim(),
        memoType: 'NEC',
        memoPriority: 'ベタ',
        memoRegistrationDate: String(ev.start || '').slice(0, 10) || '',
        memoCategory: ev.category || '社会',
        reporterName: (ev.person || '').trim() || '未設定',
        eventStart: ev.start || new Date(),
        username: 'standalone',
        scheduleId: eventId,
        materialKind: '記事',
        materialSerial: 1,
      });

      // S3出力先: 記事は article/memoフォルダに出力
      const key = `article/memo/memo-newsml-${Date.now()}.xml`;
      
      console.log('アップロード開始:', {
        key,
        bucketName: OUTPUT_BUCKET_NAME,
      });
      
      // AWS SDK S3クライアントを使用して直接アップロード
      const session = await fetchAuthSession();
      const s3Client = new S3Client({
        region: S3_REGION,
        credentials: session.credentials,
      });
      
      const command = new PutObjectCommand({
        Bucket: OUTPUT_BUCKET_NAME,
        Key: key,
        Body: xml,
        ContentType: 'application/xml;charset=UTF-8',
      });
      
      const uploadResult = await s3Client.send(command);
      
      console.log('アップロード成功:', uploadResult);
      alert(`NewsML形式のメモXMLをS3に保存しました\nBucket: ${OUTPUT_BUCKET_NAME}\nKey: ${key}`);

    } catch (error) {
      console.error('メモ一覧XML作成エラー:', error);
      console.error('エラー詳細:', {
        message: error?.message,
        code: error?.code,
        statusCode: error?.statusCode,
        response: error?.response,
      });
      alert(`メモ一覧XML作成中にエラーが発生しました。\n詳細: ${error?.message || '不明なエラー'}`);
    }
  };
};

/**
 * イベント配列をカテゴリ・エリア・作成者条件で絞り込む。
 *
 * @param {Array<Object>} events 元イベント配列
 * @param {string[]} selectedCategories 表示カテゴリ
 * @param {string[]} selectedAreas 表示エリア
 * @param {boolean} showOnlyMine 自分の予定のみ表示するか
 * @param {string|null} currentUserId ログインユーザーID
 * @returns {Array<Object>} フィルタ後イベント配列
 */
export const filterEvents = (events, selectedCategories, selectedAreas, showOnlyMine = false, currentUserId = null) => {
  return events.filter(
    (event) =>
      selectedCategories.includes(event.category) &&
      selectedAreas.includes(event.area) &&
      (!showOnlyMine || !currentUserId || event.createdBy === currentUserId)
  );
};